/**
 * Google Identity Services (GIS) — the browser half of Google sign-in.
 *
 * ## Which flow, and why
 *
 * This uses the GIS **ID-token** flow (`google.accounts.id`), not the
 * authorization-code flow. Three reasons, in order of weight:
 *
 * 1. **No client secret exists to leak.** The code flow needs a secret on the
 *    server to exchange the code; the ID-token flow is a public-client flow
 *    with nothing to exchange. Nothing secret is committed, deployed, rotated
 *    or accidentally logged, because there is nothing secret at all.
 * 2. **The mobile app lands on the same endpoint.** Native Google Sign-In on
 *    Android/iOS produces an ID token too. One server endpoint
 *    (`POST /mobile/auth/google`) serves both surfaces, so the Expo app needs
 *    no second backend.
 * 3. **No redirect, so no lost SPA state.** The credential arrives in a
 *    callback and goes straight into the existing `AuthSession`. The code flow
 *    would navigate away and back through a redirect URI, which for a static
 *    SPA means a full reload on every sign-in.
 *
 * AIM asks for identity only — no Drive, Gmail or Calendar scopes — so there
 * is no access token worth holding and no refresh token to store. The ID token
 * is spent once, at the exchange, and never persisted.
 *
 * ## What the token is worth
 *
 * Nothing, until the server says so. `credential` is opaque to this file: it
 * is never decoded here, never trusted here, and the email inside it is never
 * read here. Verification is `backend/api/app/security/google_auth_tokens.py`.
 */

import { domainBaseUrl } from "../api/config";

/** `GET /py-api/v1/auth/google/config` — the public bootstrap. */
export interface GoogleAuthConfig {
  enabled: boolean;
  /** Public by design: it ships inside the page. Never a secret. */
  clientId: string | null;
  /** False once the family admin retires password sign-in. */
  passwordLoginEnabled: boolean;
}

/**
 * What the login page falls back to when the config request fails.
 *
 * Fails SAFE **towards passwords**: a backend hiccup, a stale nginx or a
 * half-finished deploy must never present a login page with no usable way in.
 * The reverse default (assume Google, hide the password form) would turn one
 * failed GET into eight people locked out of their own finances.
 */
export const CONFIG_FALLBACK: GoogleAuthConfig = {
  enabled: false,
  clientId: null,
  passwordLoginEnabled: true,
};

const GIS_SCRIPT_URL = "https://accounts.google.com/gsi/client";
const GIS_SCRIPT_ID = "google-identity-services";
const GIS_LOAD_TIMEOUT_MS = 10_000;

export function googleConfigUrl(): string {
  return `${domainBaseUrl("auth")}/google/config`;
}

/** Read the deployment's sign-in configuration. Never throws. */
export async function fetchGoogleAuthConfig(
  fetchImpl: typeof fetch = fetch,
): Promise<GoogleAuthConfig> {
  try {
    const res = await fetchImpl(googleConfigUrl(), {
      headers: { accept: "application/json" },
    });
    if (!res.ok) return CONFIG_FALLBACK;
    const body = (await res.json()) as Partial<GoogleAuthConfig>;
    if (typeof body?.enabled !== "boolean") return CONFIG_FALLBACK;
    return {
      enabled: body.enabled,
      clientId: typeof body.clientId === "string" ? body.clientId : null,
      // An older server that predates the flag has passwords on. Defaulting
      // this to `true` rather than `false` is the same fail-safe as above.
      passwordLoginEnabled: body.passwordLoginEnabled !== false,
    };
  } catch {
    return CONFIG_FALLBACK;
  }
}

// --- the GIS script --------------------------------------------------------

/** The slice of `window.google.accounts.id` this app uses. */
export interface GoogleCredentialResponse {
  /** The ID token — a JWT this file deliberately does not decode. */
  credential?: string;
  select_by?: string;
}

export interface GoogleIdConfiguration {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  itp_support?: boolean;
}

export interface GoogleButtonOptions {
  type?: "standard" | "icon";
  theme?: "outline" | "filled_blue" | "filled_black";
  size?: "small" | "medium" | "large";
  text?: "signin_with" | "signup_with" | "continue_with" | "signin";
  shape?: "rectangular" | "pill" | "circle" | "square";
  logo_alignment?: "left" | "center";
  width?: number;
  locale?: string;
}

export interface GoogleAccountsId {
  initialize(config: GoogleIdConfiguration): void;
  renderButton(parent: HTMLElement, options: GoogleButtonOptions): void;
  disableAutoSelect(): void;
  cancel(): void;
}

interface GoogleGlobal {
  accounts: { id: GoogleAccountsId };
}

declare global {
  interface Window {
    google?: GoogleGlobal;
  }
}

let gisPromise: Promise<GoogleAccountsId> | null = null;

/**
 * Load `accounts.google.com/gsi/client` once, resolving with the id API.
 *
 * Memoised on the promise, not on a boolean: two components mounting in the
 * same tick must await ONE script tag, and the second must not resolve before
 * the first has actually finished loading.
 *
 * Rejects rather than hanging when Google is unreachable — an ad blocker, a
 * corporate proxy or an offline laptop are all normal, and the login page has
 * to be able to fall back to the password form instead of showing an empty
 * space forever.
 */
export function loadGoogleIdentityServices(): Promise<GoogleAccountsId> {
  if (gisPromise) return gisPromise;

  gisPromise = new Promise<GoogleAccountsId>((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("Google sign-in needs a browser."));
      return;
    }
    if (window.google?.accounts?.id) {
      resolve(window.google.accounts.id);
      return;
    }

    const fail = (message: string) => {
      // Cleared so a later retry (a user who re-enabled their blocker, or
      // came back online) gets a fresh attempt instead of the cached failure.
      gisPromise = null;
      reject(new Error(message));
    };

    const timer = setTimeout(
      () => fail("Google sign-in took too long to load."),
      GIS_LOAD_TIMEOUT_MS,
    );

    const onLoad = () => {
      clearTimeout(timer);
      const api = window.google?.accounts?.id;
      if (api) resolve(api);
      else fail("Google sign-in loaded but did not start.");
    };
    const onError = () => {
      clearTimeout(timer);
      fail("Google sign-in could not be reached.");
    };

    const existing = document.getElementById(GIS_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", onLoad);
      existing.addEventListener("error", onError);
      return;
    }

    const script = document.createElement("script");
    script.id = GIS_SCRIPT_ID;
    script.src = GIS_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", onLoad);
    script.addEventListener("error", onError);
    document.head.appendChild(script);
  });

  return gisPromise;
}

/**
 * Stop Google from silently re-signing this browser in.
 *
 * Called on sign-out. Without it, a user who signs out and lands back on the
 * login page can be auto-selected straight back into the session they just
 * left — which reads as "sign out is broken".
 */
export function disableGoogleAutoSelect(): void {
  try {
    window.google?.accounts?.id?.disableAutoSelect();
  } catch {
    // GIS never loaded, or already gone. Sign-out must not fail over this.
  }
}

/** Test hook — drops the memoised script promise. */
export function resetGoogleIdentityServices(): void {
  gisPromise = null;
}
